"use client";
/* eslint-disable */
import {
  Box,
  Button,
  Icon,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from "@chakra-ui/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { ReactCropperElement } from "react-cropper";
import { FileRejection, useDropzone } from "react-dropzone";
import { BiError } from "react-icons/bi";
import { FiUploadCloud, FiX } from "react-icons/fi";
import { ImageCropper } from "../ImageCropper";

export interface AvatarUploadModalProps {
  handleUpload: (file: File) => void;
  isOpen: boolean;
  onClose: () => void;
  isUploading?: boolean;
}

export function AvatarUploadModal({
  handleUpload,
  isOpen,
  onClose,
  isUploading,
}: AvatarUploadModalProps) {
  const [file, setFile] = useState<null | {
    file: File;
    preview: string | null;
  }>(null);
  const [rejectedFile, setRejectedFile] = useState<null | FileRejection>(null);
  const cropperRef = useRef<ReactCropperElement>(null);
  const onDrop = useCallback(
    (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      // Do something with the files
      if (fileRejections.length > 0) {
        setRejectedFile(fileRejections[0]);
        return;
      }
      const avatar = acceptedFiles[0];
      if (avatar) {
        const fileReader = new FileReader();
        fileReader.readAsDataURL(avatar);
        fileReader.onload = () => {
          setFile({
            file: avatar,
            preview: fileReader.result as string,
          });
        };
      }
    },
    [],
  );

  const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
    maxFiles: 1,
    accept: {
      "image/png": [".png"],
      "image/jpeg": [".jpg", ".jpeg"],
    },
    maxSize: 3 * 1000000, // 3MB
    onDrop,
    noClick: true,
  });

  useEffect(() => {
    setFile(null);
    setRejectedFile(null);
  }, []);

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add profile picture</ModalHeader>
          <ModalCloseButton rounded="full" />
          <ModalBody
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              gap: "1rem",
            }}
          >
            {file ? (
              <Box
                sx={{
                  position: "relative",
                }}
              >
                <IconButton
                  size="sm"
                  aria-label="close"
                  colorScheme="blackAlpha"
                  sx={{
                    position: "absolute",
                    top: "1rem",
                    right: "1rem",
                    zIndex: 1,
                    rounded: "full",
                  }}
                  onClick={() => setFile(null)}
                  icon={<Icon as={FiX} />}
                />
                <ImageCropper
                  ref={cropperRef}
                  image={file?.preview || ""}
                  aspectRatio={1 / 1}
                />
              </Box>
            ) : (
              <>
                {!file && !rejectedFile && (
                  <>
                    <Box
                      {...getRootProps()}
                      sx={{
                        border: "2px dashed",
                        borderColor: isDragActive
                          ? "primary.500"
                          : "surface.500",
                        borderRadius: "full",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        p: "2.5rem",
                        height: "15.625rem",
                        width: "15.625rem",
                        color: isDragActive ? "primary.500" : "surface.500",
                        gap: "1.5rem",
                        backgroundColor: isDragActive
                          ? "primary.100"
                          : "transparent",
                      }}
                    >
                      <>
                        <input {...getInputProps<any>()} />
                      </>

                      <Icon
                        as={FiUploadCloud}
                        sx={{
                          fontSize: "4rem",
                        }}
                      />
                      <Text textAlign="center">
                        Drag and drop your images here
                      </Text>
                    </Box>
                    <Text>or</Text>
                  </>
                )}

                {Boolean(rejectedFile) && (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      color: "danger.500",
                      maxW: "15.625rem",
                      gap: "1rem",
                      textAlign: "center",
                    }}
                  >
                    <Icon as={BiError} sx={{ fontSize: "6rem" }} />
                    {rejectedFile?.errors?.map((error, index) => (
                      <Text key={`upload_error_${index}`}>{error.message}</Text>
                    ))}
                    <Button
                      variant="link"
                      onClick={() => {
                        setFile(null);
                        setRejectedFile(null);
                      }}
                    >
                      Try again
                    </Button>
                  </Box>
                )}

                <Button onClick={open} colorScheme="surface">
                  Upload a photo
                </Button>
              </>
            )}
          </ModalBody>

          <ModalFooter>
            <Button
              colorScheme="danger"
              variant="ghost"
              mr={3}
              onClick={() => {
                setFile(null);
                setRejectedFile(null);
                onClose();
              }}
            >
              Cancel
            </Button>
            <Button
              isLoading={isUploading}
              isDisabled={!file?.file || isUploading}
              onClick={() => {
                if (!file) {
                  return;
                }

                cropperRef.current?.cropper
                  ?.getCroppedCanvas()
                  .toBlob((blob) => {
                    if (blob) {
                      setFile(null);
                      setRejectedFile(null);
                      handleUpload(
                        new File([blob], file?.file?.name || "image", {
                          type: file?.file.type || "image/png",
                        }),
                      );
                    }
                  });
              }}
            >
              Upload
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
